import { useNavigate } from 'react-router-dom';
import { X, Minus, Plus, Trash2, ArrowRight } from 'lucide-react';
import { Button } from './ui/button';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from './ui/sheet';
import { useCart } from '../context/CartContext';

export default function CartDrawer({ open, onClose }) {
  const navigate = useNavigate();
  const { cart, updateQuantity, removeItem, getTotal } = useCart();

  const handleCheckout = () => {
    onClose();
    navigate('/checkout');
  };

  return (
    <Sheet open={open} onOpenChange={onClose}>
      <SheetContent side="right" className="w-full sm:max-w-md">
        <SheetHeader>
          <SheetTitle>Your Cart</SheetTitle>
        </SheetHeader>

        {cart.items.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 text-center">
            <p className="text-muted-foreground mb-4">Your cart is empty</p>
            <Button variant="outline" onClick={onClose}>
              Continue Browsing
            </Button>
          </div>
        ) : (
          <div className="flex flex-col h-full">
            {cart.truckName && (
              <p className="text-sm text-muted-foreground mb-4">
                Order from <span className="font-medium text-foreground">{cart.truckName}</span>
              </p>
            )}

            <div className="flex-1 overflow-auto -mx-6 px-6">
              <div className="space-y-4">
                {cart.items.map(item => (
                  <div 
                    key={item.item_id} 
                    className="flex items-center gap-3 py-3 border-b"
                    data-testid={`cart-drawer-item-${item.item_id}`}
                  >
                    {item.photo_url && (
                      <img 
                        src={item.photo_url} 
                        alt={item.name}
                        className="w-16 h-16 rounded-lg object-cover"
                      />
                    )}
                    <div className="flex-1 min-w-0">
                      <h4 className="font-medium truncate">{item.name}</h4>
                      <p className="text-sm text-primary font-medium">
                        CA$ {(item.price * item.quantity).toFixed(2)}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button 
                        variant="outline" 
                        size="icon" 
                        className="h-8 w-8 rounded-full"
                        onClick={() => updateQuantity(item.item_id, item.quantity - 1)}
                      >
                        <Minus className="w-3 h-3" />
                      </Button>
                      <span className="w-6 text-center text-sm font-medium">
                        {item.quantity}
                      </span>
                      <Button 
                        variant="outline" 
                        size="icon" 
                        className="h-8 w-8 rounded-full"
                        onClick={() => updateQuantity(item.item_id, item.quantity + 1)}
                      >
                        <Plus className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="pt-4 mt-4 border-t space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">Subtotal</span>
                <span className="font-semibold">CA$ {getTotal().toFixed(2)}</span>
              </div>
              <Button 
                size="lg" 
                className="w-full rounded-full"
                onClick={handleCheckout}
                data-testid="checkout-btn"
              >
                Checkout
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
